import { Fragment } from 'preact';
import { BELL_VOICES, BELL_VOICE_NAMES, CLAVE, type LabInstrument } from '@salsacount/core';
import { engine } from '../engine/engine.js';
import { setBell } from './actions.js';
import { Choice } from './controls.js';
import { useEngineState } from './engine-state.js';

/** A count as the dancer says it: 2.5 is "2&". */
const say = (c: number): string => (Number.isInteger(c) ? String(c) : `${Math.floor(c)}&`);
const list = (counts: readonly (number | string)[]): string => {
  const words = counts.map((c) => (typeof c === 'number' ? say(c) : c));
  return words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : (words[0] ?? '');
};

/** Where each instrument plays, in the lab's own pattern (core's `labLoop`). One line each. */
export function describe(instrument: LabInstrument, clave: '3-2' | '2-3'): string {
  switch (instrument) {
    case 'clave': return `Hits on ${list(CLAVE[clave])}.`;
    case 'conga': return 'Slap on 2 and 6; open tones on 4, 4&, 8 and 8&; soft heel and toe strokes in between.';
    case 'bass': return 'On 2&, 4, 6& and 8. The note on 4 and on 8 belongs to the next chord: it arrives early.';
    case 'bell': return 'Open tone on 1, 3, 5 and 7; light neck strokes in between.';
  }
}

const BELL_OPTIONS = BELL_VOICE_NAMES.map((v) => ({ value: v, label: BELL_VOICES[v].label, name: `${BELL_VOICES[v].label} bell` }));
const NAMES: Record<LabInstrument, string> = { clave: 'Clave', conga: 'Conga', bass: 'Bass', bell: 'Bell' };

/** The Rhythm Lab's controls, where the player has its fix row: tempo, and an on/off row per instrument with where it plays (the bell's row also picks its voice). (The clave direction is on the stage.) */
export function Band() {
  const s = useEngineState();
  const lab = s.lab;
  if (!lab) return null;
  return (
    <div class="band">
      <div class="tempo">
        <label for="lab-tempo" class="tempo-label">Tempo</label>
        <button type="button" class="btn icon" aria-label="Slower" disabled={lab.bpm <= 140} onClick={() => void engine.setLab({ bpm: Math.max(140, lab.bpm - 5) })}>−</button>
        <input
          id="lab-tempo"
          type="range"
          min={140}
          max={220}
          step={1}
          value={lab.bpm}
          aria-valuetext={`${lab.bpm} beats per minute`}
          onChange={(e) => void engine.setLab({ bpm: Number(e.currentTarget.value) })}
        />
        <button type="button" class="btn icon" aria-label="Faster" disabled={lab.bpm >= 220} onClick={() => void engine.setLab({ bpm: Math.min(220, lab.bpm + 5) })}>+</button>
        <span class="tempo-value">{lab.bpm}</span>
      </div>
      <ul class="instruments">
        {(Object.keys(NAMES) as LabInstrument[]).map((i) => (
          <Fragment key={i}>
            <li>
              <button
                type="button"
                role="switch"
                aria-checked={lab.on[i]}
                aria-labelledby={`instrument-${i}-name`}
                aria-describedby={`instrument-${i}-line`}
                class="instrument"
                onClick={() => void engine.setLab({ on: { ...lab.on, [i]: !lab.on[i] } })}
              >
                <span class="instrument-name" id={`instrument-${i}-name`}>{NAMES[i]}</span>
                <span class="switch-knob" aria-hidden="true" data-on={lab.on[i] ? '' : undefined} />
                <span class="instrument-line" id={`instrument-${i}-line`}>{describe(i, lab.clave)}</span>
              </button>
            </li>
            {i === 'bell' && (
              <li class="voice" data-off={lab.on.bell ? undefined : ''}>
                <Choice label="Bell sound" value={lab.bell} options={BELL_OPTIONS} onChange={setBell} />
              </li>
            )}
          </Fragment>
        ))}
      </ul>
    </div>
  );
}
